import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import FormModal from "../../../component/common/FormModal";
import FormField from "../../../component/common/FormField";
import FormatChecklistUpload from "./FormatChecklistUpload";
import PendingFileList from "./PendingFileList";
import { workRequestApi } from "../api/workRequestApi";
import { runApiAction } from "../../../util/apiFeedback";
import { ds } from "../../../styles/designSystem";

const C = ds.colors;

/**
 * 과업 요청 작성 — 이 모듈의 핵심 동선.
 *
 * <pre>
 *   ① 요청 팀 선택  →  ② 요청 유형 선택  →  ③ 양식마다 한 칸씩 채우는 체크리스트
 *                                        →  ④ 담당자·제목·기한
 * </pre>
 *
 * <p>③ 이 이 기능 전체의 목적이다. 예전엔 "포맷 다운로드하세요" 를 말해야 했고, 받고 나서도
 * 서류가 빠져 "○○도 보내주세요" 를 다시 물었다. 여기서는 <b>낼 것이 목록으로 보이고</b>
 * 다 채우기 전엔 제출 버튼이 잠긴다(서버도 같은 규칙을 강제하므로 우회 불가).
 *
 * <p>양식이 하나도 등록되지 않은 유형이면 체크리스트를 만들 수 없으므로 자유 첨부로 받는다.
 *
 * <p>담당자 후보는 <b>선택한 팀 소속</b>으로 좁힌다 — 공용 EmployeeAutocomplete 는 전사 검색이라
 * 팀을 골라놓고 엉뚱한 부서 사람을 고르는 실수가 나기 쉽다.
 */
export default function WorkRequestComposeModal({ open, onClose, onCreated, departments = [], employees = [] }) {
    const [deptId, setDeptId] = useState("");
    const [typeId, setTypeId] = useState("");
    const [types, setTypes] = useState([]);
    const [typesLoading, setTypesLoading] = useState(false);

    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [note, setNote] = useState("");
    const [assignee, setAssignee] = useState(null);
    const [checklist, setChecklist] = useState({});   // { [formatId]: File }
    const [freeFiles, setFreeFiles] = useState([]);   // 양식 없는 유형용
    const [saving, setSaving] = useState(false);

    // 모달을 닫았다 열 때 이전 입력이 남아있지 않게 초기화.
    useEffect(() => {
        if (!open) return;
        setDeptId(""); setTypeId(""); setTypes([]);
        setTitle(""); setContent(""); setDueDate(""); setNote("");
        setAssignee(null); setChecklist({}); setFreeFiles([]);
    }, [open]);

    // 팀을 고르면 그 팀의 활성 유형 + 양식을 한 번에 받아온다(유형 선택 즉시 체크리스트가 뜨도록).
    useEffect(() => {
        if (!deptId) { setTypes([]); setTypeId(""); return; }
        let alive = true;
        setTypesLoading(true);
        workRequestApi.getTypes(deptId)
            .then(list => { if (alive) setTypes(Array.isArray(list) ? list : []); })
            .catch(() => { if (alive) setTypes([]); })
            .finally(() => { if (alive) setTypesLoading(false); });
        setTypeId("");
        setAssignee(null);              // 팀이 바뀌면 담당자 후보도 달라진다
        return () => { alive = false; };
    }, [deptId]);

    // 유형이 바뀌면 이전 유형의 체크리스트는 무의미하다 — 비운다.
    useEffect(() => { setChecklist({}); setFreeFiles([]); }, [typeId]);

    const selectedType = useMemo(
        () => types.find(t => String(t.id) === String(typeId)) || null,
        [types, typeId],
    );
    const formats = selectedType?.formats || [];
    const hasFormats = formats.length > 0;

    /** 담당자 후보 — 선택한 팀 소속만. */
    const assigneeOptions = useMemo(() => {
        if (!deptId) return [];
        return employees.filter(e => String(e.deptId) === String(deptId));
    }, [employees, deptId]);

    const filesReady = hasFormats
        ? formats.every(f => checklist[f.id])          // 모든 칸을 채워야 제출 가능
        : freeFiles.length > 0;
    const canSubmit = Boolean(deptId && typeId && title.trim() && assignee && filesReady);

    const handleSave = () => {
        // 체크리스트는 formatIds 와 files 를 같은 순서로 보낸다(서버가 짝으로 검증).
        const formatIds = hasFormats ? formats.map(f => f.id) : [];
        const files = hasFormats ? formats.map(f => checklist[f.id]) : freeFiles;

        return runApiAction({
            request: () => workRequestApi.create({
                title: title.trim(),
                content: content.trim() || null,
                targetDeptId: Number(deptId),
                requestTypeId: Number(typeId),
                assigneeId: assignee.id,
                dueDate: dueDate || null,
                note: note.trim() || null,
                formatIds,
            }, files),
            setLoading: setSaving,
            successMsg: "과업 요청서를 등록했습니다.",
            onSuccess: (data) => { onCreated?.(data); onClose?.(); },
        });
    };

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title="과업 요청서 작성"
            subtitle="팀과 요청 유형을 고르면 제출할 서류 목록이 나타납니다."
            maxWidth="md"
            onSave={handleSave}
            saving={saving}
            saveLabel="요청 보내기"
            saveDisabled={!canSubmit}
        >
            {/* FormModal 은 children 에 간격을 주지 않는다 — 폼이 붙어 보이지 않게 여기서 세로 간격을 준다. */}
            <Stack spacing={2.5}>
                <FormField label="요청 팀" required>
                    <TextField
                        select fullWidth size="small"
                        value={deptId}
                        onChange={(e) => setDeptId(e.target.value)}
                    >
                        {departments.map(d => (
                            <MenuItem key={d.deptId} value={d.deptId} sx={{ fontSize: 13 }}>
                                {d.deptName}
                            </MenuItem>
                        ))}
                    </TextField>
                </FormField>

                <FormField label="요청 유형" required>
                    <TextField
                        select fullWidth size="small"
                        value={typeId}
                        onChange={(e) => setTypeId(e.target.value)}
                        disabled={!deptId || typesLoading}
                        helperText={
                            !deptId ? ""
                                : typesLoading ? "유형을 불러오는 중…"
                                : types.length === 0 ? "이 팀에 등록된 요청 유형이 없습니다. 해당 팀에 양식 등록을 요청하세요."
                                : " "
                        }
                    >
                        {types.map(t => (
                            <MenuItem key={t.id} value={t.id} sx={{ fontSize: 13 }}>
                                {t.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </FormField>

                {/* ★ 유형을 고르는 순간 낼 서류가 목록으로 뜬다 — 다운로드와 업로드가 같은 줄에 있다 */}
                {selectedType && (
                    <Box>
                        {selectedType.description && (

                            <Typography sx={{
                                fontSize: 12, color: C.textSecondary, whiteSpace: "pre-wrap",
                                mb: 1.5, p: 1.25, borderRadius: 1.5,
                                bgcolor: "#fafbfc", border: `1px solid ${C.border}`,
                            }}>
                                {selectedType.description}
                            </Typography>
                        )}
                        {hasFormats ? (
                            <FormatChecklistUpload
                                formats={formats}
                                value={checklist}
                                onChange={setChecklist}
                            />
                        ) : (
                            <FormField label="요청서 파일" required>
                                <PendingFileList
                                    files={freeFiles}
                                    onChange={setFreeFiles}
                                    label="파일 첨부"
                                    helper="이 유형에는 등록된 양식이 없습니다. 자유 형식으로 첨부해 주세요."
                                />
                            </FormField>
                        )}
                    </Box>
                )}

                <FormField label="담당 직원" required>
                    <Autocomplete
                        size="small"
                        options={assigneeOptions}
                        value={assignee}
                        onChange={(_, v) => setAssignee(v)}
                        disabled={!deptId}
                        getOptionLabel={(o) => o?.name || ""}
                        isOptionEqualToValue={(o, v) => o.id === v?.id}
                        noOptionsText="이 팀에 직원이 없습니다"
                        renderOption={(props, option) => {
                            const { key, ...rest } = props;
                            return (
                                <Box component="li" key={key} {...rest} sx={{ fontSize: 13 }}>
                                    {option.name}
                                    <Box component="span" sx={{ fontSize: 11, color: C.textMuted, ml: 0.75 }}>
                                        {[option.deptName, option.positionName].filter(Boolean).join(" · ")}
                                    </Box>
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={deptId ? "담당자 이름 검색" : "먼저 요청 팀을 선택하세요"}
                            />
                        )}
                    />
                </FormField>

                <FormField label="제목" required>
                    <TextField
                        fullWidth size="small"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="예: 2026 상반기 브랜드 홍보영상 제작"
                        inputProps={{ maxLength: 200 }}
                    />
                </FormField>

                <FormField label="요청 내용">
                    <TextField
                        fullWidth multiline minRows={3} size="small"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="양식에 담기지 않는 보충 설명을 적어주세요."
                    />
                </FormField>

                <FormField label="희망 완료일">
                    <TextField
                        type="date" fullWidth size="small"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </FormField>

                <FormField label="첫 버전 메모">
                    <TextField
                        fullWidth size="small"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="예: 최초 요청본"
                    />
                </FormField>
            </Stack>
        </FormModal>
    );
}
